import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";

import api from "../../services/api";
import { translateMessageMarker } from "../../utils/messagePreview";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import {
  buildHighlightedSnippet,
  meaningfulLength,
} from "../../helpers/highlightSnippet";
import useOpenContactConversation from "../../hooks/useOpenContactConversation";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";
import ContactAvatar from "../ContactAvatar";
import NewTicketModal from "../NewTicketModal";
import MarkdownWrapper from "../MarkdownWrapper";

// Mínimo de caracteres alfanuméricos para consultar cada seção. Busca em
// mensagens usa `body LIKE '%termo%'` (não indexável), então exige um termo mais
// específico — mesmo guard-rail do backend.
const MIN_CHARS_FOR_MESSAGES = 3;
const MIN_CHARS_FOR_GROUPS = 2;
// Mensagens por lote (SearchMessagesGlobalService.LIMIT). Lote menor que isso
// com `hasMore` = o backend parou por tempo no meio do histórico, e a seção
// continua sozinha até achar resultados ou varrer o histórico inteiro — o
// usuário nunca precisa clicar para a pesquisa terminar. A única parada é
// estourar o MAX_EXECUTION_TIME `maxTimeoutRetries` vezes seguidas no mesmo
// ponto (banco sobrecarregado): aí a seção espera o clique em vez de insistir.
const MESSAGES_PAGE_SIZE = 15;
const MESSAGES_AUTO_CONTINUE = { pageSize: MESSAGES_PAGE_SIZE, maxTimeoutRetries: 2 };

const useStyles = makeStyles((theme) => ({
  wrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 0,
  },

  scroll: {
    flex: 1,
    maxHeight: "100%",
    overflowY: "auto",
    ...theme.scrollbarStyles,
    borderTop: "2px solid rgba(0, 0, 0, 0.12)",
  },

  sectionHeader: {
    padding: "10px 16px 4px",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.optionsBackground,
  },

  row: {
    cursor: "pointer",
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },

  rowTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
  },

  name: {
    fontWeight: 500,
    fontSize: 16,
  },

  time: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
  },

  secondaryText: {
    fontSize: 16,
    color: theme.palette.text.secondary,
  },

  groupHint: {
    fontSize: 16,
    color: theme.palette.primary.main,
  },

  highlight: {
    backgroundColor:
      theme.palette.type === "dark" ? "#ffd54f" : "rgba(76, 175, 80, 0.28)",
    // No escuro o texto do trecho é claro e sumiria sobre o realce.
    color: theme.palette.type === "dark" ? "#1c1c1c" : "inherit",
    borderRadius: 3,
    padding: "0 2px",
    fontWeight: 600,
  },

  emptyWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(5, 3),
    textAlign: "center",
  },

  emptyTitle: {
    fontSize: 16,
    fontWeight: 600,
  },

  emptyText: {
    fontSize: 14,
    color: "rgb(104, 121, 146)",
    marginTop: 4,
  },

  moreWrapper: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(1),
  },

  sectionLoading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(1),
  },

  messagesFooter: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(1),
  },

  searchedUntil: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
}));

const formatResultDate = (value) => {
  if (!value) return "";
  const parsed = typeof value === "string" ? parseISO(value) : new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return isSameDay(parsed, new Date())
    ? format(parsed, "HH:mm")
    : format(parsed, "dd/MM/yyyy");
};

// Mesmo limiar de 100px da rolagem infinita de TicketsListCustom.
const isNearBottom = (element) =>
  element.scrollHeight - (element.scrollTop + 100) < element.clientHeight;

const formatSearchedUntil = (value) => {
  if (!value) return "";
  const parsed = parseISO(String(value));
  if (Number.isNaN(parsed.getTime())) return "";
  return format(parsed, "dd/MM/yyyy");
};

const initialSectionState = (key) => ({
  key,
  page: 1,
  // Cursor enviado na requisição da página corrente (null = do topo).
  cursor: null,
  items: [],
  hasMore: false,
  nextCursor: null,
  searchedUntil: null,
  timedOut: false,
  lastBatchCount: 0,
});

/**
 * Carrega uma seção da busca com paginação acumulativa.
 *
 * `resetKey` identifica a busca corrente (termo + aba + filtros): quando muda, a
 * seção é zerada AINDA NA FASE DE RENDER, para que o efeito de fetch já rode com
 * página 1 e sem resíduo da busca anterior (evita o "fetch duplo" que aconteceria
 * se o reset fosse feito em outro useEffect).
 *
 * `fetchPage(page, signal, cursor)` deve ser memoizado (useCallback) com as
 * mesmas dependências que compõem o `resetKey`. O `cursor` é o `nextCursor` da
 * resposta anterior — só a seção de mensagens usa; as outras paginam por página.
 *
 * `autoContinue` ({ pageSize, maxTimeoutRetries }): quando a resposta vem
 * parcial (menos que `pageSize` itens) mas com `hasMore`, o backend parou por
 * orçamento de tempo no meio do histórico; a seção pede a próxima janela
 * sozinha, até o histórico acabar. Só para depois de `maxTimeoutRetries`
 * timeouts seguidos. A decisão sai junto com o resultado, no mesmo setState,
 * para o loading não piscar entre uma janela e outra.
 */
const useSearchSection = ({ enabled, resetKey, fetchPage, autoContinue = null }) => {
  const [state, setState] = useState(() => initialSectionState(resetKey));
  const [loading, setLoading] = useState(false);
  const seqRef = useRef(0);
  const autoRef = useRef({ key: resetKey, timeouts: 0 });
  // Requisição em andamento. A rolagem dispara onScroll em rajada: sem isto,
  // dois loadMore antes do re-render pulariam uma página inteira.
  const busyRef = useRef(false);

  if (state.key !== resetKey) {
    setState(initialSectionState(resetKey));
  }

  const { key, page, cursor } = state;

  useEffect(() => {
    if (!enabled || key !== resetKey) {
      return undefined;
    }

    const seq = ++seqRef.current;
    const controller = new AbortController();
    busyRef.current = true;
    setLoading(true);

    fetchPage(page, controller.signal, cursor)
      .then((result) => {
        if (seq !== seqRef.current) return;

        const items = Array.isArray(result.items) ? result.items : [];
        const hasMore = !!result.hasMore;
        const nextCursor = result.nextCursor ?? null;
        const timedOut = !!result.timedOut;

        if (autoRef.current.key !== key) {
          autoRef.current = { key, timeouts: 0 };
        }
        autoRef.current.timeouts = timedOut ? autoRef.current.timeouts + 1 : 0;
        const continueAuto =
          !!autoContinue &&
          hasMore &&
          items.length < autoContinue.pageSize &&
          autoRef.current.timeouts <= autoContinue.maxTimeoutRetries;

        setState((prev) => ({
          ...prev,
          items: page === 1 ? items : [...prev.items, ...items],
          hasMore,
          nextCursor,
          searchedUntil: result.searchedUntil ?? null,
          timedOut,
          lastBatchCount: items.length,
          ...(continueAuto ? { page: prev.page + 1, cursor: nextCursor } : {}),
        }));

        if (!continueAuto) {
          busyRef.current = false;
          setLoading(false);
        }
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        if (seq !== seqRef.current) return;
        busyRef.current = false;
        setLoading(false);
        toastError(err);
      });

    return () => {
      controller.abort();
    };
  }, [enabled, key, page, cursor, resetKey, fetchPage, autoContinue]);

  // Próxima página — pela rolagem até o fim, pelo "Ver mais" ou pela retomada
  // após timeouts (que zera a contagem para a continuação automática voltar).
  const loadMore = useCallback(() => {
    if (busyRef.current) return;
    busyRef.current = true;
    autoRef.current.timeouts = 0;
    setState((prev) => ({ ...prev, page: prev.page + 1, cursor: prev.nextCursor }));
  }, []);

  return {
    items: enabled ? state.items : [],
    hasMore: enabled && state.hasMore,
    loading: enabled && loading,
    searchedUntil: enabled ? state.searchedUntil : null,
    lastBatchCount: state.lastBatchCount,
    loadMore,
  };
};

/**
 * Resultados da busca da lista de atendimentos, agrupados por MOTIVO do match —
 * Contatos, Grupos em comum e Mensagens, na mesma ordem do WhatsApp. Uma seção
 * sem resultados não é renderizada.
 *
 * O escopo é a aba de onde a busca partiu: `status` (open/pending/closed) para
 * as abas de conversas, ou `null` na aba Contatos (busca em todos os contatos).
 */
const TicketsSearchResults = ({
  searchParam,
  searchBy = "all",
  status = null,
  showAll,
  selectedQueueIds,
  selectedWhatsappIds,
  selectedContactTagIds,
  onSelectContact,
  onResultCount,
  active = true,
  style,
}) => {
  const classes = useStyles();
  const history = useHistory();

  const term = String(searchParam || "").trim();
  const chars = meaningfulLength(term);

  const queueIdsKey = JSON.stringify(selectedQueueIds || []);
  const whatsappIdsKey = JSON.stringify(selectedWhatsappIds || []);
  // Tags do contato (OU). Vazio = sem filtro, e o parâmetro nem é enviado.
  const contactTagIdsKey = JSON.stringify(selectedContactTagIds || []);
  const hasContactTagFilter = contactTagIdsKey !== "[]";
  const showAllKey = showAll ? "true" : "false";

  // Uma busca é identificada por termo + aba + filtros. Qualquer mudança aqui
  // zera as três seções.
  const resetKey = `${term}|${searchBy}|${status || "all"}|${showAllKey}|${queueIdsKey}|${whatsappIdsKey}|${contactTagIdsKey}`;

  const {
    openContact,
    loadingContactId,
    pendingContact,
    closeNewTicketModal,
  } = useOpenContactConversation(onSelectContact);

  // ── Seção 1: Contatos ────────────────────────────────────────────────────
  // Nas abas de conversas a linha é o próprio atendimento (mesmo card da lista
  // normal); na aba Contatos é o contato.
  const fetchContacts = useCallback(
    async (page, signal) => {
      if (status) {
        const { data } = await api.get("/tickets", {
          params: {
            searchParam: term,
            searchBy,
            searchScope: "contact",
            status,
            showAll: showAllKey,
            pageNumber: page,
            queueIds: queueIdsKey,
            whatsappIds: whatsappIdsKey,
            ...(hasContactTagFilter ? { contactTags: contactTagIdsKey } : {}),
          },
          signal,
        });
        return {
          items: Array.isArray(data?.tickets) ? data.tickets : [],
          hasMore: !!data?.hasMore,
        };
      }

      const { data } = await api.get("/contacts/", {
        params: {
          searchParam: term,
          searchBy,
          searchScope: "contact",
          pageNumber: page,
          ...(hasContactTagFilter ? { tagIds: contactTagIdsKey } : {}),
        },
        signal,
      });
      return {
        items: Array.isArray(data?.contacts) ? data.contacts : [],
        hasMore: !!data?.hasMore,
      };
    },
    [term, searchBy, status, showAllKey, queueIdsKey, whatsappIdsKey, hasContactTagFilter, contactTagIdsKey]
  );

  const contactsSection = useSearchSection({
    enabled: active && chars >= 1,
    resetKey,
    fetchPage: fetchContacts,
  });

  // ── Seção 2: Grupos em comum ─────────────────────────────────────────────
  const fetchGroups = useCallback(
    async (_page, signal) => {
      const { data } = await api.get("/contacts/common-groups", {
        params: { searchParam: term },
        signal,
      });
      return { items: Array.isArray(data?.groups) ? data.groups : [], hasMore: false };
    },
    [term]
  );

  const groupsSection = useSearchSection({
    // O switch "somente nome do contato" restringe a busca ao cadastro do
    // contato — grupos em comum e mensagens ficam de fora.
    enabled: active && searchBy !== "contact" && chars >= MIN_CHARS_FOR_GROUPS,
    resetKey,
    fetchPage: fetchGroups,
  });

  // ── Seção 3: Mensagens ───────────────────────────────────────────────────
  // Paginação por cursor keyset: o backend varre o histórico em janelas e
  // devolve onde parou (nextCursor) e até que data já procurou (searchedUntil).
  const fetchMessages = useCallback(
    async (_page, signal, cursor) => {
      const { data } = await api.get("/messages/search", {
        params: {
          searchParam: term,
          status: status || undefined,
          showAll: showAllKey,
          ...(cursor ? { cursor } : {}),
          queueIds: queueIdsKey,
          whatsappIds: whatsappIdsKey,
          ...(hasContactTagFilter ? { contactTags: contactTagIdsKey } : {}),
        },
        signal,
      });
      return {
        items: Array.isArray(data?.messages) ? data.messages : [],
        hasMore: !!data?.hasMore,
        nextCursor: data?.nextCursor ?? null,
        searchedUntil: data?.searchedUntil ?? null,
        timedOut: !!data?.timedOut,
      };
    },
    [term, status, showAllKey, queueIdsKey, whatsappIdsKey, hasContactTagFilter, contactTagIdsKey]
  );

  const messagesSection = useSearchSection({
    enabled: active && searchBy !== "contact" && chars >= MIN_CHARS_FOR_MESSAGES,
    resetKey,
    fetchPage: fetchMessages,
    autoContinue: MESSAGES_AUTO_CONTINUE,
  });

  // Badge da aba: quantas conversas/contatos a busca encontrou.
  useEffect(() => {
    if (typeof onResultCount === "function") {
      onResultCount(contactsSection.items.length);
    }
  }, [contactsSection.items.length, onResultCount]);

  const handleMessageClick = (message) => {
    const uuid = message?.ticket?.uuid;
    if (!uuid) return;
    history.push(`/tickets/${uuid}`, {
      jumpToMessageId: String(message.id),
      jumpToMessageTs: Date.now(),
    });
  };

  const handleCloseNewTicketModal = (ticket) => {
    closeNewTicketModal();
    if (ticket?.uuid) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const renderSectionHeader = (label, count) => (
    <div className={classes.sectionHeader}>
      {label}
      {count > 0 ? ` (${count})` : ""}
    </div>
  );

  const renderMore = (section) =>
    section.hasMore && !section.loading ? (
      <div className={classes.moreWrapper}>
        <Button size="small" color="primary" onClick={section.loadMore}>
          {i18n.t("ticketsSearch.showMore")}
        </Button>
      </div>
    ) : null;

  // Lote parcial com `hasMore`: o histórico ainda não foi varrido inteiro e a
  // seção está continuando sozinha — "Pesquisado até" é o progresso. O botão
  // só aparece parado nesse estado depois de timeouts seguidos. Lote cheio não
  // tem botão: a próxima página vem ao rolar até o fim da lista.
  const messagesPartial =
    messagesSection.hasMore && messagesSection.lastBatchCount < MESSAGES_PAGE_SIZE;

  const renderMessagesFooter = () => {
    if (!messagesSection.hasMore) return null;

    const searchedUntil = messagesPartial
      ? formatSearchedUntil(messagesSection.searchedUntil)
      : "";

    if (!searchedUntil) return null;

    return (
      <div className={classes.messagesFooter}>
        {searchedUntil && (
          <Typography component="span" className={classes.searchedUntil}>
            {i18n.t("ticketsSearch.searchedUntil", { date: searchedUntil })}
          </Typography>
        )}
        {messagesPartial && !messagesSection.loading && (
          <Button size="small" color="primary" onClick={messagesSection.loadMore}>
            {i18n.t("ticketsSearch.searchOlder")}
          </Button>
        )}
      </div>
    );
  };

  const renderContactRow = (contact, secondary) => (
    <ListItem
      key={`contact-${contact.id}`}
      className={classes.row}
      onClick={() => openContact(contact)}
      disabled={loadingContactId === contact.id}
    >
      <ListItemAvatar>
        {loadingContactId === contact.id ? (
          <CircularProgress size={40} />
        ) : (
          <ContactAvatar contact={contact} />
        )}
      </ListItemAvatar>
      <ListItemText
        disableTypography
        primary={
          <span className={classes.rowTitle}>
            <Typography
              noWrap
              component="span"
              variant="body2"
              color="textPrimary"
              className={classes.name}
            >
              {contact.name}
            </Typography>
            {contact.lastMessageTime && (
              <Typography component="span" className={classes.time}>
                {formatResultDate(contact.lastMessageTime)}
              </Typography>
            )}
          </span>
        }
        secondary={secondary}
      />
    </ListItem>
  );

  const anyLoading =
    contactsSection.loading || groupsSection.loading || messagesSection.loading;

  const hasAnyResult =
    contactsSection.items.length > 0 ||
    groupsSection.items.length > 0 ||
    messagesSection.items.length > 0;

  // Rolagem infinita: só a seção que fecha a lista carrega sozinha ao chegar no
  // fim — Mensagens quando aparece; senão, Contatos. A de cima (Contatos com
  // Mensagens abaixo) mantém o "Ver mais", porque o botão fica no meio da lista
  // e a rolagem nunca o alcança como "fim". Nada dispara enquanto alguma seção
  // carrega (evita puxar contatos antes de as mensagens chegarem), nem no
  // estado parado por timeouts, que espera o clique.
  const messagesVisible = messagesSection.items.length > 0 || messagesSection.hasMore;
  const tailSection = messagesVisible ? messagesSection : contactsSection;
  const tailLoadMore = tailSection.loadMore;
  const tailCanLoadMore =
    !anyLoading &&
    tailSection.hasMore &&
    !(tailSection === messagesSection && messagesPartial);

  const scrollRef = useRef(null);

  const handleScroll = (e) => {
    if (!tailCanLoadMore) return;
    if (isNearBottom(e.currentTarget)) tailLoadMore();
  };

  // Lista que não enche a área visível nunca dispara onScroll: depois de cada
  // carga, se o fim já está à vista, pede a próxima página sozinho.
  useEffect(() => {
    if (!tailCanLoadMore || !scrollRef.current) return;
    if (isNearBottom(scrollRef.current)) tailLoadMore();
  }, [
    tailCanLoadMore,
    tailLoadMore,
    contactsSection.items.length,
    messagesSection.items.length,
  ]);

  return (
    <Paper elevation={0} className={classes.wrapper} style={style}>
      <NewTicketModal
        modalOpen={!!pendingContact}
        initialContact={pendingContact}
        onClose={handleCloseNewTicketModal}
      />

      <Paper
        square
        elevation={0}
        className={classes.scroll}
        ref={scrollRef}
        onScroll={handleScroll}
      >
        <List style={{ paddingTop: 0 }}>
          {/* ── Contatos ── */}
          {contactsSection.items.length > 0 && (
            <>
              {renderSectionHeader(
                i18n.t("ticketsSearch.sections.contacts"),
                contactsSection.items.length
              )}
              {status
                ? contactsSection.items.map((ticket) => (
                    <TicketListItem ticket={ticket} key={`ticket-${ticket.id}`} />
                  ))
                : contactsSection.items.map((contact) =>
                    renderContactRow(
                      contact,
                      contact.lastMessage ? (
                        <Typography
                          noWrap
                          component="span"
                          className={classes.secondaryText}
                        >
                          <MarkdownWrapper>{translateMessageMarker(contact.lastMessage)}</MarkdownWrapper>
                        </Typography>
                      ) : (
                        !contact.isGroup &&
                        contact.number && (
                          <Typography
                            component="span"
                            className={classes.secondaryText}
                          >
                            {contact.number}
                          </Typography>
                        )
                      )
                    )
                  )}
              {tailSection !== contactsSection && renderMore(contactsSection)}
            </>
          )}

          {/* ── Grupos em comum ── */}
          {groupsSection.items.length > 0 && (
            <>
              {renderSectionHeader(
                i18n.t("ticketsSearch.sections.commonGroups"),
                groupsSection.items.length
              )}
              {groupsSection.items.map((group) =>
                renderContactRow(
                  group.contact,
                  <Typography component="span" className={classes.groupHint}>
                    {i18n.t("ticketsSearch.alsoInGroup", {
                      name: group.matchedParticipantName,
                    })}
                  </Typography>
                )
              )}
            </>
          )}

          {/* ── Mensagens ── */}
          {/* Sem itens mas com histórico por varrer, a seção aparece mesmo
              assim: é onde ficam o "pesquisado até" e o botão para continuar. */}
          {(messagesSection.items.length > 0 || messagesSection.hasMore) && (
            <>
              {renderSectionHeader(
                i18n.t("ticketsSearch.sections.messages"),
                messagesSection.items.length
              )}
              {messagesSection.items.map((message) => (
                <ListItem
                  key={`message-${message.id}`}
                  className={classes.row}
                  onClick={() => handleMessageClick(message)}
                >
                  <ListItemAvatar>
                    <ContactAvatar contact={message.contact} />
                  </ListItemAvatar>
                  <ListItemText
                    disableTypography
                    primary={
                      <span className={classes.rowTitle}>
                        <Typography
                          noWrap
                          component="span"
                          variant="body2"
                          color="textPrimary"
                          className={classes.name}
                        >
                          {message.contact?.name || message.contact?.number}
                        </Typography>
                        <Typography component="span" className={classes.time}>
                          {formatResultDate(message.createdAt)}
                        </Typography>
                      </span>
                    }
                    secondary={
                      <Typography component="span" className={classes.secondaryText}>
                        {/* Em grupo, mostra quem escreveu — como no WhatsApp. */}
                        {message.fromMe
                          ? `${i18n.t("messageSearchDrawer.you")}: `
                          : message.contact?.isGroup && message.senderName
                          ? `${message.senderName}: `
                          : ""}
                        {buildHighlightedSnippet(
                          message.body,
                          term,
                          classes.highlight
                        )}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
              {renderMessagesFooter()}
            </>
          )}

          {anyLoading && <TicketsListSkeleton />}

          {!anyLoading && !hasAnyResult && !messagesSection.hasMore && (
            <div className={classes.emptyWrapper}>
              <span className={classes.emptyTitle}>
                {i18n.t("ticketsSearch.noResults")}
              </span>
              <p className={classes.emptyText}>
                {i18n.t("ticketsSearch.noResultsHint", { term })}
              </p>
            </div>
          )}
        </List>
      </Paper>
    </Paper>
  );
};

export default TicketsSearchResults;
